import * as Yup from "yup";
import { IEC_CODES, NORWAY_CODES, REGEX, SWEDEN_CODES } from "../components/Common/constants";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { getCountryLabel, getNormalizedCountry } from "../components/Common/utils";

export function useEUFormConfig({ user, modifyUser }) {
  const [initialValues, setInitialValues] = useState({});

  const { t } = useTranslation("common");


  const defaultValues = {
    // Personal Info
    firstName: "",
    fullMiddleName: "",
    lastName: "",
    paAddress: "",
    paPostcode: "",
    countryUK: "",
    paCountry: "",
    paExistingInfo: "",
    gender: "",
    city: "",
    // PA Screening
    day: "",
    month: "",
    year: "",
    nationality: "",
    cityOfBirth: "",
    countryOfBirth: "",
    personalIdNumber: "",
    password: "",
    supportingFiles: [],
    paScreeningFiles: [],
    // PA Security/Login
    motherMaidenName: "",
    verificationPin: "",
    memorableDate: "",
    clueMemorableDate: "",
    // Contact Info
    phoneNumber: "",
    emailId: "",
    // Company Info
    bussRegName: "",
    bussRegAddress: "",
    bussRegPostcode: "",
    bussRegCountry: "",
    companyType: "",
    organisationNumber: "",
    // Correspondence Info
    empBussName: "",
    corrAddress: "",
    corrPostcode: "",
    corrCountry: "",
    modifypaPermissionOnline: false,
    // Politically Exposed Person Info
    notPoliticallyExposedPerson: false,
    familyOfPoliticallyExposedPerson: false,
    politicallyExposedPerson: false,
    politicallyExposedPersonDropdown: "",
    pepTitle: "",
    pepCountry: "",
    pepOrganization: "",
    //Photo ID Info
    photoIdNumber: "",
    typeOfDocument: "",
    photoExpiryDate: "",
    currentFirstName: "",
    currentFullMiddleName: "",
    currentFullLastName: "",
    currentCompanyEmail: "",
  };

  useEffect(() => {
    if (user?.country) {
      setInitialValues({
        ...defaultValues,
        ...user,
      });
    }
  }, [user?.country]);

  const country = user?.country || initialValues.paCountry || "";
  const countryNorm = getNormalizedCountry(country);
  const isSweden = SWEDEN_CODES.includes(countryNorm);
  const isNorway = NORWAY_CODES.includes(countryNorm);
  const isIec = IEC_CODES.map((code) => getNormalizedCountry(code)).includes(countryNorm);
  const isSwedenNorwayOrIecModifyUser = modifyUser && (isSweden || isNorway || isIec);
  const fileShape = Yup.object().shape({
    documentName: Yup.string().required(),
    base64Content: Yup.string().required(),
    mimeType: Yup.string().required(),
  });

  const conditionalCheck = (requiredKey = null, { regex = null, regexErrKey = null } = {}, isOptional = false) => {
    if (isSwedenNorwayOrIecModifyUser && !isOptional) {
      return Yup.string().test(
        `${requiredKey || "optional"}-format-or-empty`,
        t(regexErrKey || requiredKey),
        (val) => !val || (regex ? regex.test(val) : true)
      );
    }
    let s = Yup.string();
    if (requiredKey) s = s.required(t(requiredKey));
    if (regex) s = s.matches(regex, t(regexErrKey));
    return s;
  };

  const swNoMandatoryCheck = (requiredKey = null, { regex = null, regexErrKey = null } = {}) => {
    if (!isSwedenNorwayOrIecModifyUser) return Yup.string();
    let s = Yup.string();
    if (requiredKey) s = s.required(t(requiredKey));
    if (regex) s = s.matches(regex, t(regexErrKey));
    return s;
  };

  const screeningFieldCheck = (requiredKey, { regex = null, regexErrKey = null } = {}) => {
    if (isSwedenNorwayOrIecModifyUser) {
      return Yup.string().test(
        `${requiredKey}-format-or-empty`,
        t(regexErrKey || requiredKey),
        (val) => !val || (regex ? regex.test(val) : true)
      );
    }
    if ((isSweden || isNorway) && !modifyUser) {
      return Yup.string().when(['personalIdNumber'], ([personalIdNumber], schema) => {
        if (personalIdNumber) {
          return regex
            ? schema.test(`${requiredKey}-format-optional`, t(regexErrKey), val => !val || regex.test(val))
            : schema;
        }
        let s = schema.required(t(requiredKey));
        if (regex) s = s.matches(regex, t(regexErrKey));
        return s;
      });
    }
    let s = Yup.string().required(t(requiredKey));
    if (regex) s = s.matches(regex, t(regexErrKey));
    return s;
  };

  const countryLabel = getCountryLabel(user?.country, t);

  const contactInfoFields = {
    INTERNATIONALEUROCARD: {
      bussRegName: Yup.string().required(t("newuser.businessRegNameRequired")),
      bussRegAddress: Yup.string().required(t("newuser.businessRegAddressRequired")),
      bussRegPostcode: conditionalCheck("newuser.businessRegPostcodeRequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.businessRegPostcodeError" }, true),
      bussRegCountry: Yup.string().required(t("newuser.businessRegCountryRequired")),
    },
    DEFAULT: {
      bussRegName: Yup.string().required(t("newuser.bussregnamerequired")),
      bussRegAddress: Yup.string().required(t("newuser.bussregaddressrequired")),
      bussRegPostcode: conditionalCheck("newuser.bussregaddressrequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.bussRegPostcodeerror" }),
      bussRegCountry: Yup.string().required(t("newuser.bussregcountryrequired")),
    }
  };

  const correspondenceInfoFields = {
    INTERNATIONALEUROCARD: {
      empBussName: conditionalCheck("newuser.businessBussNameRequired"),
      corrPostcode: conditionalCheck("newuser.corrBusinessPostcodeRequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.corrpostcodeerror" }, true),
      corrAddress: conditionalCheck("newuser.corraddressrequired"),
      corrCountry: conditionalCheck("newuser.corrcountryrequired"),
    },
    DEFAULT: {
      empBussName: Yup.string(),
      corrAddress: Yup.string(),
      corrPostcode: Yup.string().test(
        "corrPostcode-format",
        t("newuser.corrpostcodeerror"),
        (val) => !val || REGEX.NUMBER.test(val)
      ),
      corrCountry: Yup.string(),
    }
  };

  const validationSchema = Yup.object({
    firstName: conditionalCheck("newuser.fnrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.fnerror" }),
    fullMiddleName: Yup.string(),
    lastName: conditionalCheck("newuser.lnrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.lnerror" }),
    paAddress: conditionalCheck("newuser.paaddressrequired"),
    paPostcode: conditionalCheck("newuser.papostcoderequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.papostcodeerror" }),
    countryUK: conditionalCheck("newuser.countryrequired"),
    paCountry: conditionalCheck("newuser.pacountryrequired"),
    paExistingInfo: conditionalCheck(),
    gender: isSweden ? Yup.string() : conditionalCheck("newuser.genderrequired"),
    city: (isSweden || isNorway) && !modifyUser
      ? Yup.string()
          .required(t("newuser.cityrequired"))
          .matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.cityerror"))
      : (isSweden || isNorway)
        ? Yup.string().matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.cityerror"))
        : Yup.string(),
    day: screeningFieldCheck("newuser.dayrequired", { regex: REGEX.TWO_DIGIT_NUMBER, regexErrKey: "newuser.dayerror" })
      .test("valid-day", t("newuser.dayerror"), val => !val || (Number(val) >= 1 && Number(val) <= 31)),
    month: screeningFieldCheck("newuser.monthrequired", { regex: REGEX.TWO_DIGIT_NUMBER, regexErrKey: "newuser.montherror" })
      .test("valid-month", t("newuser.monthValid"), val => !val || (Number(val) >= 1 && Number(val) <= 12)),
    year: screeningFieldCheck("newuser.yearRequired", { regex: REGEX.FOUR_DIGIT_NUMBER, regexErrKey: "newuser.validYear" }),
    nationality: screeningFieldCheck("newuser.nationalityRequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.nationalityError" }),
    cityOfBirth: screeningFieldCheck("newuser.ciobrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.cioberror" }),
    countryOfBirth: screeningFieldCheck("newuser.cnobrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.cnoberror" }),
    personalIdNumber: Yup.string().test(
      "personalId-conditional",
      t("newuser.personalidnumbererror"),
      function (val) {
        if (isSwedenNorwayOrIecModifyUser) {
          return !val || REGEX.PERSONAL_ID_NUMBER.test(val);
        }
        const { day, month, year, nationality, cityOfBirth, countryOfBirth } = this.parent;
        const hasScreeningFields = day || month || year || nationality || cityOfBirth || countryOfBirth;
        if (!hasScreeningFields && !val) {
          return this.createError({ message: t("newuser.personalidrequiredconditional") });
        }
        if (val && !REGEX.PERSONAL_ID_NUMBER.test(val)) {
          return this.createError({ message: t("newuser.personalidnumbererror") });
        }
        return true;
      }
    ),
    password: conditionalCheck("newuser.passwordrequired").min(8, t("newuser.passwordminlength")),
    paScreeningFiles: modifyUser
      ? Yup.array().of(fileShape)
      : (isIec || isSweden || isNorway)
        ? Yup.array().min(1, t("access.uploaderror")).of(fileShape)
        : Yup.array().of(fileShape),
    // PA Security/Login
    verificationPin: conditionalCheck("newuser.verificationpinukrequired", { regex: REGEX.FOUR_DIGIT_NUMBER, regexErrKey: "newuser.verificationpinukerror" }),
    memorableDate: conditionalCheck("newuser.memorableDateUkRequired", { regex: REGEX.FOUR_DIGIT_NUMBER, regexErrKey: "newuser.memorableDateUkError" }),
    motherMaidenName: isIec
      ? conditionalCheck("newuser.mothersMaidenNameRequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.mothersMaidenNameError" })
      : Yup.string(),
    clueMemorableDate: conditionalCheck("newuser.cluememorabledateukrequired"),
    // Contact Info
    phoneNumber: isIec ? Yup.string().required(t("newuser.phonenumberrequired")) : conditionalCheck("newuser.phonenumberrequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.phonenumbererror" }),
    emailId: isIec ? Yup.string().required(t("newuser.emailrequire")) : conditionalCheck("newuser.emailrequire", { regex: REGEX.EMAIL, regexErrKey: "newuser.emailerror" }),
    // Company Info
    ...contactInfoFields[countryLabel],
    organisationNumber: (isSweden || isNorway)
      ? Yup.string()
          .required(t("newuser.organisationnumberrequired"))
          .test(
            "organisation-number-format",
            t("newuser.organisationnumbererror"),
            function (val) {
              if (!val) return false;
              if (isSweden) {
                return REGEX.NUMBER_HYPHEN.test(val);
              }
              return REGEX.NUMBER.test(val);
            }
          )
      : Yup.string(),
    // Correspondence Info
    ...correspondenceInfoFields[countryLabel],
    notPoliticallyExposedPerson: (isSweden || isNorway) && !modifyUser
      ? Yup.boolean().test(
          "pep-selection-required",
          t("newuser.pepSelectionRequired"),
          function (val) {
            const { familyOfPoliticallyExposedPerson, politicallyExposedPerson } = this.parent;
            return val || familyOfPoliticallyExposedPerson || politicallyExposedPerson;
          }
        )
      : Yup.boolean(),
    familyOfPoliticallyExposedPerson: Yup.boolean(),
    politicallyExposedPerson: Yup.boolean(),
    politicallyExposedPersonDropdown: Yup.string()
      .when(
        ['politicallyExposedPerson', 'familyOfPoliticallyExposedPerson'],
        ([isPep, isFamily], schema) =>
          isPep || isFamily
            ? schema
                .required(t("newuser.pepPositionRequired"))
                .notOneOf(["dropdown_menu"], t("newuser.pepPositionRequired"))
            : schema
      ),
    pepTitle: Yup.string()
      .when(
        ['politicallyExposedPerson', 'familyOfPoliticallyExposedPerson'],
        ([isPep, isFamily], schema) =>
          isPep || isFamily
            ? schema
                .required(t("newuser.pepTitleRequired"))
                .matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.titleerror"))
            : schema.matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.titleerror"))
      ),
    pepCountry: Yup.string()
      .when(
        ['politicallyExposedPerson', 'familyOfPoliticallyExposedPerson'],
        ([isPep, isFamily], schema) =>
          isPep || isFamily
            ? schema
                .required(t("newuser.pepCountryRequired"))
                .matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.countryerror"))
            : schema.matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.countryerror"))
      ),
    pepOrganization: Yup.string()
      .when(
        ['politicallyExposedPerson', 'familyOfPoliticallyExposedPerson'],
        ([isPep, isFamily], schema) =>
          isPep || isFamily
            ? schema
                .required(t("newuser.pepOrganizationRequired"))
                .matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.organizationerror"))
            : schema.matches(REGEX.LETTER_SPACE_HYPHEN, t("newuser.organizationerror"))
      ),
    photoIdNumber: isIec
      ? conditionalCheck("newuser.photoIdNumberRequired", { regex: REGEX.NUMBER, regexErrKey: "newuser.photoIdNumberError" })
      : Yup.string(),
    typeOfDocument: isIec && !modifyUser
      ? conditionalCheck("newuser.typeOfDocumentRequired")
      : Yup.string().nullable(),
    photoExpiryDate: isIec
      ? conditionalCheck("newuser.photoExpiryDateRequired", { regex: REGEX.DDMMYY, regexErrKey: "newuser.photoExpiryDateError" })
      : Yup.string(),
    currentFirstName: swNoMandatoryCheck("newuser.fnrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.fnerror" }),
    currentFullMiddleName: Yup.string(),
    currentFullLastName: swNoMandatoryCheck("newuser.lnrequired", { regex: REGEX.LETTER_SPACE_HYPHEN, regexErrKey: "newuser.lnerror" }),
    currentCompanyEmail: swNoMandatoryCheck("newuser.emailrequire", { regex: REGEX.EMAIL, regexErrKey: "newuser.emailerror" }),
  });

  return { initialValues, validationSchema };
}
